import React from 'react';
import { Link } from 'react-router-dom';

import { chargerClub, type Club, type MembreClub } from '../Auth/moi.ts';

/* Accueil d'un administrateur de club qui n'entraine pas (ni coach ni
   athlete) : synthese du club, sur le modele de l'accueil (Main.tsx). */

type TileData = {
  id: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  value?: React.ReactNode;
};

const containerStyle: React.CSSProperties = {
  display: 'grid',
  // Quatre tuiles sur une ligne ; les deux listes se partagent la ligne suivante.
  gridTemplateColumns: 'repeat(4, 1fr)',
  gridAutoRows: 'auto',
  gap: 16,
  padding: 16,
  maxWidth: 960,
  margin: '0 auto',
};

const tileStyle: React.CSSProperties = {
  background: 'var(--fond-tuile-doux)',
  border: '1px solid var(--bord-tuile)',
  borderRadius: 8,
  padding: 12,
  boxShadow: '0 1px 3px rgba(16,24,40,0.05)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: 100,
};

// Liste sur deux colonnes de la grille, contenu aligne en haut.
const listeStyle: React.CSSProperties = {
  gridColumn: 'span 2',
  justifyContent: 'flex-start',
};

const titleStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--texte)',
  marginBottom: 8,
  fontWeight: 600,
};

const valueStyle: React.CSSProperties = {
  fontSize: 20,
  color: 'var(--texte)',
  fontWeight: 700,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--texte-secondaire)',
  marginTop: 6,
};

const ligneStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 8,
  fontSize: 14,
  color: 'var(--texte)',
  padding: '4px 0',
  borderTop: '1px solid var(--bord-tuile)',
};

const lienClub: React.CSSProperties = { color: 'inherit', textDecoration: 'none' };

function pluriel(n: number, mot: string): string {
  return `${n} ${mot}${n > 1 ? 's' : ''}`;
}

const nomMembre = (m: MembreClub) => m.nom || m.email;

function Tuile({ t }: { t: TileData }): JSX.Element {
  return (
    <div style={tileStyle}>
      <div>
        <div style={titleStyle}>{t.title}</div>
        <div style={valueStyle}>{t.value}</div>
      </div>
      {t.subtitle ? <div style={subtitleStyle}>{t.subtitle}</div> : null}
    </div>
  );
}

export default function AccueilClub(): JSX.Element {
  const [club, setClub] = React.useState<Club | null | undefined>(undefined);
  const [erreur, setErreur] = React.useState<string | null>(null);
  React.useEffect(() => {
    chargerClub()
      .then(setClub)
      .catch((e: Error) => setErreur('Club indisponible : ' + e.message));
  }, []);

  if (erreur) return <div style={{ ...containerStyle, display: 'block', paddingTop: 96 }}>{erreur}</div>;
  if (club === undefined) return <div style={{ ...containerStyle, display: 'block', paddingTop: 96 }}>…</div>;
  if (club === null) {
    return (
      <div style={{ ...containerStyle, display: 'block', paddingTop: 96 }}>
        Tu n'administres aucun club. <Link to="/club">Voir la page Club</Link>
      </div>
    );
  }

  const membres = club.membres;
  const acceptes = membres.filter((m) => !m.enAttente);
  const coachs = acceptes.filter((m) => m.coach);
  const athletes = acceptes.filter((m) => m.athlete);
  const sansCoach = athletes.filter((m) => !m.idCoachSuivi);
  const admins = acceptes.filter((m) => m.administrateur);
  const bibliothecaires = coachs.filter((m) => m.bibliothecaire);
  const invitations = membres.filter((m) => m.enAttente);
  const suivis = athletes.length - sansCoach.length;

  const tuiles: TileData[] = [
    {
      id: 'coachs',
      title: <Link to="/club" style={lienClub}>Coachs</Link>,
      value: coachs.length,
      subtitle: invitations.length > 0
        ? `${pluriel(invitations.length, 'invitation')} en attente`
        : 'Aucune invitation en attente',
    },
    {
      id: 'athletes',
      title: <Link to="/club" style={lienClub}>Athlètes</Link>,
      value: athletes.length,
      subtitle: athletes.length === 0
        ? 'Aucun athlète dans le club'
        : sansCoach.length > 0
          ? `${suivis} suivi${suivis > 1 ? 's' : ''} · ${sansCoach.length} sans coach`
          : 'Tous suivis par un coach',
    },
    {
      id: 'bibliotheque',
      title: <Link to="/club" style={lienClub}>Bibliothèque du club</Link>,
      value: pluriel(club.nbSeancesBibliotheque, 'séance'),
      subtitle: bibliothecaires.length > 0
        ? `${pluriel(bibliothecaires.length, 'bibliothécaire')} : ${bibliothecaires.map(nomMembre).join(', ')}`
        : 'Aucun bibliothécaire : la bibliothèque ne peut plus être modifiée',
    },
    {
      id: 'admins',
      title: <Link to="/club" style={lienClub}>Administrateurs</Link>,
      value: admins.length,
      subtitle: admins.map((m) => nomMembre(m) + (m.moi ? ' (toi)' : '')).join(', '),
    },
  ];

  const coachsTries = [...coachs].sort((a, b) => b.nbAthletes - a.nbAthletes || nomMembre(a).localeCompare(nomMembre(b)));

  return (
    <div style={{ paddingTop: 80 }}>
      <div style={containerStyle}>
        <div style={{ gridColumn: '1 / -1' }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--texte)' }}>
          <Link to="/club" style={lienClub}>{club.nom}</Link>
        </div>
        <div style={subtitleStyle}>
          {club.personnel ? 'Club personnel' : 'Synthèse du club'} · <Link to="/club">Gérer le club</Link>
        </div>
        </div>
        {tuiles.map((t) => <Tuile key={t.id} t={t} />)}
        <div style={{ ...tileStyle, ...listeStyle }}>
          <div style={titleStyle}>Athlètes par coach</div>
          {coachsTries.length === 0
            ? <div style={subtitleStyle}>Aucun coach dans le club. <Link to="/club">Inviter un coach</Link></div>
            : coachsTries.map((m) => (
              <div key={m.id} style={ligneStyle}>
                <span>{nomMembre(m)}{m.bibliothecaire ? ' · bibliothécaire' : ''}</span>
                <span style={{ fontWeight: 600 }}>{pluriel(m.nbAthletes, 'athlète')}</span>
              </div>
            ))}
        </div>
        <div style={{ ...tileStyle, ...listeStyle }}>
          <div style={titleStyle}>À traiter</div>
          {sansCoach.length === 0 && invitations.length === 0 && (
            <div style={subtitleStyle}>Rien à traiter : tous les athlètes ont un coach.</div>
          )}
          {sansCoach.map((m) => (
            <div key={m.id} style={ligneStyle}>
              <span>{nomMembre(m)}</span>
              <Link to="/club" style={{ fontSize: 12 }}>Athlète sans coach</Link>
            </div>
          ))}
          {invitations.map((m) => (
            <div key={m.id} style={ligneStyle}>
              <span>{m.email}</span>
              <span style={{ fontSize: 12, color: 'var(--texte-secondaire)' }}>Invitation en attente</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
